const API_BASE = '/api';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { body, headers: customHeaders, ...rest } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(customHeaders as Record<string, string>),
    };

    const config: RequestInit = {
      ...rest,
      headers,
      credentials: 'include',
    };

    if (body !== undefined) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    const data = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      const errorBody = data?.error as Record<string, unknown> | undefined;
      throw new ApiRequestError(
        (errorBody?.message as string) || 'An unexpected error occurred',
        (errorBody?.code as string) || 'UNKNOWN_ERROR',
        response.status,
        errorBody?.field as string | undefined,
      );
    }

    return data as T;
  }

  async get<T>(
    endpoint: string,
    paramsOrHeaders?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<T> {
    const isHeaders = paramsOrHeaders && !Object.values(paramsOrHeaders).some((v) => v.includes('?'));
    const actualHeaders = headers || (isHeaders ? paramsOrHeaders : undefined);
    const actualParams = isHeaders ? undefined : paramsOrHeaders;
    const url = actualParams
      ? `${endpoint}?${new URLSearchParams(actualParams).toString()}`
      : endpoint;
    return this.request<T>(url, { method: 'GET', headers: actualHeaders });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(
    endpoint: string,
    formData: FormData,
    _onProgress?: (progress: number) => void,
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    const data = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      const errorBody = data?.error as Record<string, unknown> | undefined;
      throw new ApiRequestError(
        (errorBody?.message as string) || 'Upload failed',
        (errorBody?.code as string) || 'UPLOAD_ERROR',
        response.status,
        errorBody?.field as string | undefined,
      );
    }

    return data as T;
  }
}

export class ApiRequestError extends Error {
  code: string;
  status: number;
  field?: string;

  constructor(message: string, code: string, status: number, field?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

export const api = new ApiClient(API_BASE);
